import {
    AccountType,
    ActionTypes,
    SelectOption,
    WorkflowRejectCodes,
    doesWorkflowRejectCodeExist,
    getWorkflowRejectCodeMessage
} from '@tassat/common-definitions';
import { FieldValidationAction } from '@tassat/forms';
import { IFormValues } from '@tassat/forms/src/AccountCreationForm/types';
import { AccountRecord, FirmRecord } from '@tassat/fred-definitions';
import { Dispatch, SetStateAction } from 'react';

import { CORE_SUCCESS_CODE } from 'Lib/constant';
import { timeoutPromise } from 'Lib/TimeOutPromise';
import Worker from 'Lib/worker/PrimusWorker';

export const validateDdaAccount = async (
    values: IFormValues,
    firm: FirmRecord,
    API_TIMEOUT: string | number,
    dispatchDdaAccountAction: Dispatch<FieldValidationAction<SelectOption>>
) => {
    dispatchDdaAccountAction({ type: 'VALIDATING_FIELD' });
    const query = Worker.getInstance()
        .validateBdAccount({
            BrokerDealerAccountNumber: values.Account,
            FirmName: firm.Name,
            LegalEntityId: firm.Lei
        });
    let error: string;
    try {
        const response = await timeoutPromise(API_TIMEOUT, query);

        if (response.data.Status === CORE_SUCCESS_CODE) {
            //Successful
            dispatchDdaAccountAction({ type: 'SERVER_SUCCESS' });

            return;
        } else {
            // Using generic error but including cases below in the event Product wants more details for user
            const genericError = 'Invalid BD Account, please check account number or status and try again';
            switch (response.data.Status) {
                // Invalid Account
                case WorkflowRejectCodes[97]:
                    error = genericError;
                    break;
                // Bad Firm
                case WorkflowRejectCodes[69]:
                    error = genericError;
                    break;
                // Invalid LEI
                case WorkflowRejectCodes[177]:
                    error = genericError;
                    break;
                default:
                    error = genericError;
                    break;
            }

            return error;
        }
    } catch (error) {
        return 'Time out, please check account number or status and try again';
    }
};

export const getTransactions = (options: {
    Condition: string;
    Sort: string;
    PageNo: number;
    RecordPerPage: number;
}) => Worker.getInstance()
    .getTransactions(options);

export const updateFirmAccount = (
    firmId: string,
    accountId: string,
    values: any
) => Worker.getInstance()
    .updateFirmAccount(firmId, accountId, values);

export const submitAccountForm = async (values: IFormValues, options: {
    actionType: ActionTypes;
    firmId: string;
    account?: AccountRecord;
    setSubmitError: Dispatch<SetStateAction<string>>;
    onClose: () => void;
}): Promise<void> => {
    const {
        actionType,
        firmId,
        account,
        setSubmitError,
        onClose
    } = options;

    // FRED-3390,FRED-3391 Add trim to DB Account Name and Notes
    // Trim not needed for Email/Account as validation does not allow spaces at all
    const accountValues = {
        FirmId: firmId,
        AccountName: values.AccountName.trim(),
        Account: values.Account,
        Email: values.Email,
        Notes: values.Notes.trim(),
        ACGId1: '0',
        ACGId2: '0',
        ACGId3: '0',
        AccessControl: false,
        WalletName1: '',
        WalletName2: '',
        ShortName: '',
        AccountType: AccountType.CLIENT_BDA
    };

    try {
        let response = { data: undefined };

        if (actionType === ActionTypes.add) {
            response = await Worker.getInstance()
                .createFirmAccount(firmId, accountValues);
        } else if (actionType === ActionTypes.edit) {
            if (!account) {
                setSubmitError('Could not find account record, please try again');

                return;
            }

            response = await Worker.getInstance()
                .updateFirmAccount(firmId, account.AccountId, {
                    ...accountValues,
                    IsLocked: account.IsLocked,
                    IsClosed: account.IsClosed,
                    IsDisabled: account.IsDisabled,
                    IsDeleted: account.IsDeleted
                });
        }

        if (response.data && response.data.Status === 48) {
            onClose();
        } else if (response.data && response.data.Status === 49) {
            setSubmitError(`Could not ${actionType === ActionTypes.add ? 'add' : actionType === ActionTypes.edit ? 'edit' : ''} Account: ${response.data.Reason || 'Something went wrong'}`);
        } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
            setSubmitError(`Could not ${actionType === ActionTypes.add ? 'add' : actionType === ActionTypes.edit ? 'edit' : ''} Account: ${getWorkflowRejectCodeMessage(response.data.Status)}`);
        }
    } catch (error) {
        setSubmitError('Something went wrong');
    }
};